import React, { type FC, useState } from 'react';

import cn from 'classnames';

import { Button } from '@shared/ui/button';
import { Heading } from '@shared/ui/heading';
import { Tabs } from '@shared/ui/tabs';
import { type HistoricalCall, useCallsList } from '@features/call';
import { CallItem } from '@entities/call-item';
import { InfoModal } from '@entities/info-modal';
import IconArrowRight from '@assets/img/icons/arrow-right.svg';
import IconClose from '@assets/img/icons/close-x.svg';
import IconPhonePark from '@assets/img/icons/phone-park.svg';

import { ALL_CALLS, MISSED_CALLS, resumeParkingModalLabels } from '../config';
import { useSidePanel } from '../hooks';
import type { SidePanelProps } from '../model/types';

import styles from './styles.module.scss';

export const SidePanel: FC<SidePanelProps> = ({ onCall, onCallUp, onHangup, onResume }) => {
	const [showParkingModal, setShowParkingModal] = useState(false);
	const [selectedParkingCallId, setSelectedParkingCallId] = useState<string | null>(null);

	const { isOpenPanel, isOpenParkingPanel, setIsOpenParkingPanel } = useSidePanel();
	const { parkingCalls, missedCalls, allAcceptedCalls } = useCallsList();

	const handleOpenResumeParkingCallModal = (dialogId: string) => {
		setSelectedParkingCallId(dialogId);
		setShowParkingModal(true);
	};

	const handleCloseParkingPanel = () => setIsOpenParkingPanel(false);
	const handleOpenParkingPanel = () => setIsOpenParkingPanel(true);

	const handleResumeParkingCall = async () => {
		if (!selectedParkingCallId) {
			setShowParkingModal(false);
			return;
		}

		const dialog = parkingCalls.find(
			(call) => call.callId === selectedParkingCallId || call.id === selectedParkingCallId
		);

		setShowParkingModal(false);
		setSelectedParkingCallId(null);

		if (!dialog) {
			return;
		}

		setIsOpenParkingPanel(false);

		try {
			await onCallUp(dialog, false);
		} catch (error) {
			console.error('Failed to resume parking call', error);
		}
	};

	const renderCall = (call: HistoricalCall) => (
		<CallItem
			key={call.id}
			callInfo={call}
			state={call.state}
			user={{ displayName: call.displayName, id: call.id, aor: call.aor }}
			onCall={onCall}
			onHangup={onHangup}
			onResume={onResume}
		/>
	);

	const callTabs = [
		{ label: ALL_CALLS, calls: allAcceptedCalls },
		{ label: MISSED_CALLS, calls: missedCalls },
	].map(({ label, calls }) => ({
		label,
		content: calls.map((call) => renderCall(call)),
	}));

	return (
		<div
			className={cn(
				styles.Container,
				{ [styles.hide]: !isOpenPanel },
				{ [styles.parkingOpen]: isOpenParkingPanel }
			)}
		>
			<div className={styles.ContactMenu}>
				{parkingCalls.length > 0 && (
					<div className={styles.ParkingBlock} onClick={handleOpenParkingPanel}>
						<div className={styles.Title}>
							<span className={styles.Icon} dangerouslySetInnerHTML={{ __html: IconPhonePark }} />
							<span>Припаркованные звонки: {parkingCalls.length}</span>
						</div>

						<span className={styles.Icon} dangerouslySetInnerHTML={{ __html: IconArrowRight }} />
					</div>
				)}
				<Tabs tabs={callTabs} />
			</div>

			<div className={styles.ParkingMenu}>
				<div className={styles.Headline}>
					<Heading noMargin className={styles.HeadlineText} size="h4">
						<span
							className={styles.HeadlineIcon}
							dangerouslySetInnerHTML={{ __html: IconPhonePark }}
						/>
						Припаркованные звонки: {parkingCalls.length}
					</Heading>
					<Button buttonType="icon" svg={IconClose} onClick={handleCloseParkingPanel} />
				</div>
				<div className={styles.PanelContent}>
					{parkingCalls.map((dialog) => (
						<CallItem
							key={dialog.id}
							callButtonText="Ответить"
							callInfo={{ startedAt: dialog.startedWith }}
							state="parking"
							user={{
								aor: dialog.remoteTarget,
								displayName: dialog.remoteDisplayName,
								id: dialog.callId,
							}}
							onCall={() => handleOpenResumeParkingCallModal(dialog.callId)}
						/>
					))}
				</div>

				<InfoModal
					handleApply={handleResumeParkingCall}
					modalInfo={resumeParkingModalLabels}
					setShowModal={setShowParkingModal}
					showModal={showParkingModal}
					title="Ответ на припаркованный вызов"
				/>
			</div>
		</div>
	);
};
